/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * The product-listing view preference — grid or list (CRCB2B-87).
 *
 * This module is deliberately isomorphic: the type, the values and the parser are needed by
 * the control in the browser as well as by the loader on the server. The halves that need
 * `context` — reading the request's cookie and serializing a `Set-Cookie` — live in
 * `view-preference.server.ts`, so importing the type into a client component does not drag
 * server-only cookie code into the client bundle.
 *
 * ## Why a cookie and not a URL parameter
 *
 * Three sources point here and a fourth forbids the alternative:
 *
 * - The AC asks for the layout to update "immediately without page reload" and for the
 *   selection to persist "for the session (minimum) … optionally via local storage or user
 *   preference". It never mentions the URL.
 * - `CLAUDE.md` rule 15: persistent cross-request state goes in cookies, not `localStorage`,
 *   because cookies are SSR-compatible and avoid hydration mismatches.
 * - `CLAUDE.md` rule 12 reserves the URL for state that must survive refresh **and be
 *   shareable** — filters, sort, pagination. This describes the shopper, not the result set,
 *   and should follow them to the next listing page, which a URL parameter cannot do.
 * - The promoted `segmented-buttons` spec: "A selection that changes the URL SHALL be treated
 *   as navigation and bound to a `NavLink` set carrying `aria-current`, not to ToggleGroup or
 *   Tabs" — and that binding was explicitly not built. A URL-backed toggle could therefore
 *   not use the design-system segmented button at all.
 *
 * Consequence worth keeping: because this is not a URL parameter, toggling cannot trigger
 * revalidation. Nothing needs adding to `getSearchWithoutClientOnlyParams()` or to the
 * `productGridDataKey` derivation, and a toggle issues no product search.
 */

/** The two presentations the listing supports. */
export const PRODUCT_LISTING_VIEWS = ['grid', 'list'] as const;

export type ProductListingView = (typeof PRODUCT_LISTING_VIEWS)[number];

/**
 * Grid is the fallback for absent, unrecognised or malformed values.
 *
 * This matters beyond first visits: a cookie already set in a shopper's browser outlives any
 * revert of this code, so an unknown value must degrade rather than throw.
 */
export const DEFAULT_PRODUCT_LISTING_VIEW: ProductListingView = 'grid';

/** Cookie name. `createCookie` namespaces it per site, so this is the un-suffixed base. */
export const PRODUCT_LISTING_VIEW_COOKIE = 'sram_plp_view';

/** One year — a display preference should outlive a browsing session. */
export const PRODUCT_LISTING_VIEW_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/** Form field name shared by the control and the resource action. */
export const PRODUCT_LISTING_VIEW_FIELD = 'view';

/** Narrows an unknown value to a supported view. */
export function isProductListingView(value: unknown): value is ProductListingView {
    return typeof value === 'string' && (PRODUCT_LISTING_VIEWS as readonly string[]).includes(value);
}

/**
 * Resolves any input to a supported view, falling back to {@link DEFAULT_PRODUCT_LISTING_VIEW}.
 *
 * Use this on the read path, where tolerating a stale or hand-edited cookie is correct. Do NOT
 * use it on the write path — an unrecognised submitted value is a client bug, and coercing it
 * would silently persist a preference the shopper never chose.
 */
export function parseProductListingView(value: unknown): ProductListingView {
    return isProductListingView(value) ? value : DEFAULT_PRODUCT_LISTING_VIEW;
}
